import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Cpu, Globe2, Users, Send, Loader2 } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import OpenRoles from "@/components/site/OpenRoles";
import { COMPANY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  "Marketing Executive",
  "AI / Automation Engineering",
  "Web / App Development",
  "Digital Marketing",
  "Design",
  "Sales / Business Development",
  "Other",
];

const EXPECTATIONS = [
  { icon: Cpu, label: "Real AI & automation projects" },
  { icon: Globe2, label: "Clients across the UK & India" },
  { icon: Users, label: "Small, focused, ambitious team" },
];

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-electric focus:ring-4 focus:ring-electric/10";

const labelClass =
  "mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500";

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  role_interest: "",
  message: "",
};

export default function Careers() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > 5 * 1024 * 1024) {
      toast.error("File too large — please choose a file under 5MB.");
      e.target.value = "";
      return;
    }
    setCvFile(f);
  };

  useEffect(() => {
    document.title = `Careers | ${COMPANY.name}`;
    window.scrollTo(0, 0);
    return () => {
      document.title =
        "Prata Technologies Ltd | AI Automation, WhatsApp AI Chatbots & Digital Solutions UK";
    };
  }, []);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Please enter a valid email address.";
    if (!form.role_interest) er.role_interest = "Please select an area of interest.";
    if (form.message.trim().length < 10)
      er.message = "Please tell us a little more (min 10 characters).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      let cv = {};
      if (cvFile) {
        const fd = new FormData();
        fd.append("file", cvFile);
        const { data } = await axios.post(`${API}/applications/cv`, fd);
        cv = { cv_path: data.cv_path, cv_filename: data.cv_filename };
      }
      await axios.post(`${API}/applications`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        role_interest: form.role_interest,
        message: form.message.trim(),
        ...cv,
      });
      toast.success("Application received. Thank you — we'll keep your details on file.");
      setForm(initialForm);
      setCvFile(null);
      setFileKey((k) => k + 1);
    } catch {
      toast.error("Something went wrong sending your application. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const FieldError = ({ id }) =>
    errors[id] ? (
      <p data-testid={`careers-error-${id}`} className="mt-1.5 text-xs text-red-600" role="alert">
        {errors[id]}
      </p>
    ) : null;

  return (
    <>
      <Header />
      <main data-testid="careers-page" className="bg-white pb-24 pt-36 md:pt-44">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-electric">
              Careers at Prata Technologies
            </p>
            <h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-slate-900 sm:text-5xl">
              Help Build Technology That Moves Businesses{" "}
              <span className="text-gradient-blue">Forward</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">
              {COMPANY.name} is a UK-based, AI-first technology company building AI
              automation, chatbots, voice agents, websites, apps and custom software
              for businesses across the UK and India.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-3">
            {EXPECTATIONS.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08}>
                <div
                  data-testid={`careers-point-${i}`}
                  className="flex items-center gap-2.5 rounded-full border border-slate-200 bg-slate-50 px-5 py-2.5"
                >
                  <item.icon className="h-4 w-4 text-electric" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-slate-700">{item.label}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <Reveal>
              <OpenRoles
                onApply={(title) => {
                  setForm((f) => ({ ...f, role_interest: title }));
                  setErrors((er) => ({ ...er, role_interest: undefined }));
                }}
              />
              <p className="mt-6 text-sm text-slate-600">
                Prefer email? Write to us directly at{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="careers-email-link"
                  className="font-medium text-electric transition-colors duration-300 hover:text-electric-bright"
                >
                  {COMPANY.email}
                </a>
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <form
                id="apply"
                onSubmit={submit}
                noValidate
                data-testid="careers-form"
                className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60 md:p-10"
              >
                <h2 className="font-display text-xl font-medium text-slate-900">
                  Send Your Application
                </h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="careers_full_name" className={labelClass}>Full Name *</label>
                    <input id="careers_full_name" data-testid="careers-input-full-name" className={inputClass} value={form.full_name} onChange={set("full_name")} placeholder="Your name" autoComplete="name" />
                    <FieldError id="full_name" />
                  </div>
                  <div>
                    <label htmlFor="careers_email" className={labelClass}>Email Address *</label>
                    <input id="careers_email" type="email" data-testid="careers-input-email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
                    <FieldError id="email" />
                  </div>
                  <div>
                    <label htmlFor="careers_phone" className={labelClass}>Phone Number</label>
                    <input id="careers_phone" type="tel" data-testid="careers-input-phone" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="+44 ..." autoComplete="tel" />
                  </div>
                  <div>
                    <label htmlFor="careers_role" className={labelClass}>Area of Interest *</label>
                    <select id="careers_role" data-testid="careers-select-role" className={inputClass} value={form.role_interest} onChange={set("role_interest")}>
                      <option value="">Select an area</option>
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <FieldError id="role_interest" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="careers_message" className={labelClass}>About You *</label>
                    <textarea id="careers_message" rows={5} data-testid="careers-input-message" className={`${inputClass} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about yourself, your experience and what you'd love to work on..." />
                    <FieldError id="message" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="careers_cv" className={labelClass}>
                      CV / Resume (PDF or Word, max 5MB)
                    </label>
                    <input
                      key={fileKey}
                      id="careers_cv"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      data-testid="careers-input-cv"
                      onChange={onFileChange}
                      className="w-full rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 transition-colors duration-300 file:mr-4 file:rounded-full file:border-0 file:bg-electric file:px-5 file:py-2 file:text-sm file:font-medium file:text-white hover:border-electric/50"
                    />
                    {cvFile && (
                      <p className="mt-2 text-xs text-slate-500" data-testid="careers-cv-name">
                        Selected: {cvFile.name}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="careers-submit-btn"
                  className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-electric px-8 py-4 font-medium text-white transition-[background-color,box-shadow,transform,opacity] duration-300 hover:scale-[1.01] hover:bg-electric-bright hover:shadow-[0_0_28px_rgba(37,99,235,0.4)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Application
                      <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
